/** @format */

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import LabelInput from "../../../shared/components/labelInput";
import Tq4Mapper from "../mappers/tq4Mapper";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import QuoteStep from "../enums/QuoteStep";
import MonthYearField from "../../../shared/components/monthYearField";
import { handleNext } from "../../../shared/utils/navigationUtils";
import {
  fetchLicenceAuth,
  fetchNcdService,
} from "../../../shared/services/simpleListItemsService";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import RadioGroup from "../../../shared/components/RadioGroup";
import SimpleAccordion from "../../../shared/components/simpleAccordion";
import FieldStack from "../../../shared/components/FieldStack";
import QuoteStepHeading from "../../../shared/components/QuoteStepHeading";

const Tq4 = () => {
  // Store
  // Form Setup
  const {
    register,
    formState: { errors, isValid },
    watch,
    control,
    setValue,
    trigger,
    handleSubmit,
  } = useForm({
    defaultValues: Tq4Mapper.get(),
  });

  // State
  const [lic, setlic] = useState([]);
  const [bonus, setBonus] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const convertArrayFormat = (array) => {
    if (!Array.isArray(array)) {
      return [];
    }
    return array.map((item) => ({
      value: item.listItemId,
      label: item.listItemDescription,
    }));
  };

  // Effects
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [licData, bonusData] = await Promise.all([
          fetchLicenceAuth(),
          fetchNcdService(),
        ]);

        setlic(licData);
        setBonus(bonusData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const ncdYears = Number(watch("bonus")?.value);

  useEffect(() => {
    if (!Number.isFinite(ncdYears) || ncdYears < 1) {
      setValue("ncdEntitlementReasonId", null);
      setValue("NoClaimBonus", null);
    }
  }, [ncdYears, setValue]);

  const datePassed = watch("datePassed");
  useEffect(() => {
    if (datePassed) trigger("datePassed");
  }, [datePassed]);

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ4_ENTERED);
  }, []);

  // Event Handlers
  const onSubmit = (data) => {
    Tq4Mapper.set(data);
    if (isValid) {
      TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ4_NEXT_CLICKED);
    }
    handleNext(QuoteStep.TQ5);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <QuoteFormWrapper>
          <QuoteStepHeading
            title="Cover Details"
            description="Please tell us about your driving history."
          />

          <FieldStack className="tw-text-sm">
            <LabelInput
              id="bonus"
              label="How many years no claims discount (NCD) do you have?"
              type="select"
              options={convertArrayFormat(bonus)}
              control={control}
              placeholder="Select no claim bonus"
              register={register("bonus", { required: true })}
              errors={errors.bonus}
              watch={watch("bonus")}
              trigger={trigger}
              isSearchable={true}
              isLoading={isLoading}
            />
            {ncdYears >= 1 && (
              <RadioGroup
                name="ncdEntitlementReasonId"
                mainLabel="How did you earn your no claims discount?"
                register={register}
                trigger={trigger}
                errors={errors.ncdEntitlementReasonId}
                labelYes="Private Hire or Public Hire"
                labelNo="Private Car"
                yesValue="13"
                noValue="11"
                onNo={() => setValue("NoClaimBonus", null)}
              />
            )}
            {ncdYears >= 4 && watch("ncdEntitlementReasonId") === "13" && (
              <RadioGroup
                name="NoClaimBonus"
                mainLabel="Would you like to protect your no claim bonus?"
                register={register}
                trigger={trigger}
                errors={errors.NoClaimBonus}
              />
            )}

            <LabelInput
              id="LicenseAuth"
              label="Licensing authority"
              type="select"
              options={convertArrayFormat(lic)}
              control={control}
              placeholder="Type a few letters to search"
              register={register("LicenseAuth", { required: true })}
              watch={watch("LicenseAuth")}
              trigger={trigger}
              isSearchable={true}
              isLoading={isLoading}
              errors={errors.LicenseAuth}
            />

            <MonthYearField
              label="When did you obtain your Private Hire License or Taxi Badge?"
              control={control}
              monthID="datePassedMonth"
              yearID="datePassedYear"
              errors={{
                datePassedMonth: errors.datePassedMonth,
                datePassedYear: errors.datePassedYear,
              }}
              trigger={trigger}
              watch={watch}
              register={register}
              setValue={setValue}
            />

            <RadioGroup
              name="vehicleUse"
              mainLabel="In addition to working Private Hire for Uber, do you work Public Hire or as a Hackney Carriage?"
              register={register}
              trigger={trigger}
              errors={errors.vehicleUse}
              yesValue={17}
              noValue={15}
            />

            <RadioGroup
              name="insuranceDeclined"
              mainLabel="Have you ever had insurance declined, cancelled or special terms imposed?"
              register={register}
              trigger={trigger}
              errors={errors.insuranceDeclined}
            />

            <RadioGroup
              name="convictedCharged"
              mainLabel="Have you ever been convicted, charged, or received a police
              caution for any crime (excluding traffic offenses)?"
              children={
                <SimpleAccordion title="More on unspent convictions">
                  <div>
                    After a specified period of time some criminal convictions
                    are considered 'spent' and no longer need to be disclosed to
                    your insurer. If you have a criminal conviction, you can use
                    UNLOCK&apos;s online tool to calculate if it is spent:{" "}
                    <a
                      className="tw-cursor-pointer"
                      href="https://unlock.org.uk/disclosure-calculator/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      https://unlock.org.uk/disclosure-calculator/
                    </a>
                  </div>
                </SimpleAccordion>
              }
              register={register}
              trigger={trigger}
              errors={errors.convictedCharged}
            />

            <RadioGroup
              name="addAdditionalDriver"
              mainLabel="Are you the only driver to be insured on the vehicle?"
              register={register}
              trigger={trigger}
              errors={errors.addAdditionalDriver}
              labelYes="Yes"
              labelNo="No"
              yesValue="No"
              noValue="Yes"
            />
            {watch("addAdditionalDriver") === "Yes" ? (
              <p>You can only add drivers who hold a valid Taxi badge</p>
            ) : null}
          </FieldStack>
        </QuoteFormWrapper>
        <FormNavigationButtons prevStep={QuoteStep.TQ3} isValid={isValid} />
      </form>
    </>
  );
};

export default Tq4;
